import 'keen-slider/keen-slider.min.css';

import { useKeenSlider } from 'keen-slider/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import Video from 'react-scale/components/ui/video';
import cn from 'classnames';
import Image from 'next/image';

const VideoShowcase = ({ videos }) => {
  const [loaded, setLoaded] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  const [sliderRef, slider] = useKeenSlider({
    loop: false,
    centered: true,
    slides: {
      perView: 1,
      spacing: 32
    },
    mode: 'snap',
    created() {
      setLoaded(true);
    },
    slideChanged(s) {
      setCurrentSlide(s.track.details.rel);
    }
  });

  return (
    <>
      <div className="w-screen left-1/2 relative transform px-7 -translate-x-1/2 overflow-hidden md:hidden">
        <div ref={sliderRef} className="keen-slider w-full !overflow-visible">
          {videos.map((video, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <div key={i} className="keen-slider__slide">
              <Video url={video.src} poster={video.cover} smallButton />
            </div>
          ))}
        </div>
        {loaded && slider && (
          <div className="justify-center mx-auto max-w-max flex space-x-2 mt-12">
            {[...Array(slider.current.slides.length).keys()].map(idx => (
              <span
                key={idx}
                onClick={() => slider.current?.moveToIdx(idx)}
                className={cn(
                  'cursor-pointer rounded-full w-[10px] h-[10px]',
                  currentSlide === idx ? 'bg-white' : 'bg-[#37383B]'
                )}
              />
            ))}
          </div>
        )}
        <p className="mb-4">Speakers</p>
        {videos[currentSlide].badges.map(badge => (
          <Badge {...badge} />
        ))}
      </div>

      <VideoShowcaseDesktop videos={videos} />
    </>
  );
};

const VideoShowcaseDesktop = ({ videos = [] }) => (
  <div className="space-y-10 hidden md:block">
    <div className="grid grid-cols-2 gap-x-10">
      {videos.map((video, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <VideoComp key={i} video={video} />
      ))}
    </div>
  </div>
);

const VideoComp = ({ video }) => {
  const [overflow, setOverflow] = useState({ overflowLeft: false, overflowRight: false });
  const ref = useRef(null);
  const forwardIntervalRef = useRef(null);
  const backIntervalRef = useRef(null);

  useEffect(() => {
    if (!ref.current) return;

    const tabsRef = ref?.current;

    const handleResizeOrScroll = () => {
      const { scrollWidth, offsetWidth, scrollLeft } = tabsRef;
      const spaceLeft = offsetWidth + scrollLeft;

      if (scrollWidth === offsetWidth) {
        setOverflow({ overflowLeft: false, overflowRight: false });
      } else if (spaceLeft === scrollWidth) {
        setOverflow({ overflowLeft: true, overflowRight: false });
      } else if (spaceLeft === offsetWidth) {
        setOverflow({ overflowLeft: false, overflowRight: true });
      } else {
        setOverflow({ overflowLeft: true, overflowRight: true });
      }
    };

    const resizeObserver = new ResizeObserver(handleResizeOrScroll);

    tabsRef.addEventListener('scroll', handleResizeOrScroll);
    resizeObserver.observe(tabsRef);

    handleResizeOrScroll();

    return () => {
      tabsRef?.removeEventListener('scroll', handleResizeOrScroll);
      resizeObserver.disconnect();
    };
  }, [ref]);

  const scrollX = useCallback(
    step => {
      if (!ref?.current) return;

      ref.current.scroll({
        left: ref.current.scrollLeft + step,
        behavior: 'smooth'
      });
    },
    [ref]
  );

  const back = () => {
    if (backIntervalRef?.current) return;

    backIntervalRef.current = setInterval(() => scrollX(-100), 100);
  };
  const forward = () => {
    if (forwardIntervalRef?.current) return;

    forwardIntervalRef.current = setInterval(() => scrollX(100), 100);
  };
  const clearBack = () => {
    clearInterval(backIntervalRef.current);
    backIntervalRef.current = null;
  };
  const clearForward = () => {
    clearInterval(forwardIntervalRef.current);
    forwardIntervalRef.current = null;
  };

  useEffect(
    () => () => {
      clearBack();
      clearForward();
    },
    []
  );

  return (
    <div className="space-y-4">
      <Video smallButton url={video.src} poster={video.cover} />
      <p className="font-aeonik text-2xl xl:w-2/3">{video.title}</p>
      <div className="relative w-full">
        <div
          className={cn(
            'opacity-0 transition-opacity duration-300 absolute left-0 top-0 bottom-[1px] w-1/5 bg-gradient-to-r from-black z-10',
            overflow.overflowLeft && 'opacity-100'
          )}
          onMouseEnter={back}
          onMouseLeave={clearBack}
        />
        <div
          className={cn(
            'opacity-0 transition-opacity duration-300 absolute right-0 top-0 bottom-[1px] w-1/5 bg-gradient-to-l from-black z-10',
            overflow.overflowRight && 'opacity-100'
          )}
          onMouseEnter={forward}
          onMouseLeave={clearForward}
        />
        <div className="flex space-x-4 overflow-auto no-scrollbar relative" ref={ref}>
          {video.badges.map((badge, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <div className="flex min-w-max space-x-4 items-center" key={index}>
              <Image
                src={badge.picture?.src ?? '/'}
                position="fixed"
                width={50}
                height={50}
                alt={badge.picture?.alt ?? 'speaker profile picture'}
              />
              <div className="space-y-2">
                <b className="font-semibold">{badge.name}</b>
                <p className="text-sm">{badge.position}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const Badge = ({ name, position }) => (
  <div className="bg-gray-900 rounded-lg mb-4 last:mb-0 py-3 md:py-2 px-4 space-y-1 md:mr-4">
    <b className="font-semibold lg:text-lg text-sm">{`${name} `}</b>
    <p className="md:inline-block text-sm lg:text-base">
      <span className="hidden md:inline-block">|</span>
      {` ${position}`}
    </p>
  </div>
);

export default VideoShowcase;
